import { IMPORT_PAGE_MAX, type ImportOutcome } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import type { OutcomeKind, ReviewKind } from '@enp/domain/migration';
import {
  Button,
  createColumnHelper,
  DataTable,
  EmptyState,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type ColumnDef,
} from '@enp/ui';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Search, SearchX, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ImportsBreadcrumb, runLabel } from '../../../../../imports/components.js';
import {
  costGapText,
  DIFF_SECTION,
  NOT_CLASSIFIED_REASON,
  OUTCOME,
  OUTCOME_ORDER,
  refOfProductKey,
} from '../../../../../imports/labels.js';
import {
  outcomesByRefQuery,
  outcomesQuery,
  recordsSearchSchema,
  runQuery,
} from '../../../../../imports/queries.js';
import { useAccountTimeZone } from '../../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../../shell/page.js';
import { ErrorPanel } from '../../../../../shell/route-states.js';

export const Route = createFileRoute('/_app/imports/runs/$runId/records')({
  validateSearch: recordsSearchSchema,
  component: RecordsPage,
});

const ALL = 'all';

function outcomeDetail(o: ImportOutcome['outcome']): string {
  switch (o.kind) {
    case 'mapped':
      return 'A product of its own';
    case 'merged_into_family':
      return 'Part of another product’s family';
    case 'state_mapping':
      return `The ${o.state} state of a product`;
    case 'ignored':
      return o.reason;
    case 'could_not_classify':
      return `Not classified: ${NOT_CLASSIFIED_REASON[o.reason] ?? o.reason.replace(/_/g, ' ')}`;
  }
}

/** The review queue kind that decides a record that could not be classified. */
const REVIEW_KIND_OF: Record<string, ReviewKind | undefined> = {
  category_role_unknown: 'category_role_unknown',
  twin_ambiguous: 'twin_ambiguous',
  twin_unpaired: 'twin_unpaired',
  state_group_uncertain: 'state_group_uncertain',
};

function DecideLink({ runId, outcome }: { runId: string; outcome: ImportOutcome['outcome'] }) {
  if (outcome.kind !== 'could_not_classify') return null;
  const kind = REVIEW_KIND_OF[outcome.reason];
  return (
    <Link
      to="/imports/runs/$runId/review"
      params={{ runId }}
      search={kind ? { kind } : {}}
      className="inline-flex items-center gap-1 text-sm font-medium text-primary-text underline-offset-2 hover:underline"
    >
      Decide it
      <ArrowRight aria-hidden className="size-3.5" />
    </Link>
  );
}

const col = createColumnHelper<ImportOutcome>();
// TanStack's documented DataTable typing: columns carry heterogeneous value types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Columns = ColumnDef<ImportOutcome, any>[];
const columnsFor = (runId: string, costGaps?: Map<string, string>): Columns => [
  col.accessor('name', {
    header: 'Name',
    enableSorting: false,
    meta: { width: 'min-w-56' },
    cell: ({ getValue }) => (
      <span className="font-medium text-fg">{getValue<string | null>() ?? 'No name'}</span>
    ),
  }),
  col.accessor('sku', {
    header: 'SKU',
    enableSorting: false,
    cell: ({ getValue }) =>
      getValue<string | null>() ? (
        <span className="whitespace-nowrap font-mono text-sm">{getValue<string>()}</span>
      ) : (
        <span className="text-muted">None</span>
      ),
  }),
  col.accessor('record_kind', {
    header: 'Record',
    enableSorting: false,
    cell: ({ getValue }) => (getValue<string>() === 'item' ? 'Item' : 'Variant'),
  }),
  col.display({
    id: 'outcome',
    header: 'What happened',
    cell: ({ row }) => (
      <span className="flex flex-col items-start gap-0.5 leading-tight">
        <span className="text-fg">{OUTCOME[row.original.outcome.kind].label}</span>
        <span className="text-sm text-muted">{outcomeDetail(row.original.outcome)}</span>
        <DecideLink runId={runId} outcome={row.original.outcome} />
      </span>
    ),
  }),
  // The products without a standard cost: why (UI1A-204).
  ...(costGaps
    ? [
        col.display({
          id: 'cost',
          header: 'Why no cost',
          cell: ({ row }) => {
            const reason = costGaps.get(row.original.source_ref);
            return reason ? (
              <span className="text-fg">{costGapText(reason)}</span>
            ) : (
              <span className="text-muted">—</span>
            );
          },
        }),
      ]
    : []),
];

const CHANGE_TITLE = { added: 'New', changed: 'Changed', removed: 'Gone' } as const;

function RecordsPage() {
  const { runId } = Route.useParams();
  const timeZone = useAccountTimeZone();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const run = useQuery(runQuery(runId));
  // The products the cost roll-up can't cost (UI1A-204), by their source record.
  const costMode = search.cost === 'missing';
  const notComputable = run.data?.report?.costCoverage.notComputable;
  const costGaps = useMemo(
    () =>
      costMode && notComputable
        ? new Map(notComputable.map((p) => [refOfProductKey(p.productKey), p.reason]))
        : undefined,
    [costMode, notComputable],
  );
  const columns = useMemo(() => columnsFor(runId, costGaps), [runId, costGaps]);
  // Records behind a count of "Changes since the last load" (UI1A-17): gone ones are named
  // by the import that was loaded last.
  const change =
    search.change && search.section ? { change: search.change, section: search.section } : null;
  const changedRefs = (
    change
      ? (run.data?.diff?.sections[change.section][change.change] ?? []).map((k) =>
          k.replace(/^src:/, ''),
        )
      : costGaps
        ? [...costGaps.keys()]
        : []
  ).filter((r) => /^(item|variant):/.test(r));
  // A list of named records (changes, products without a cost) instead of a filter.
  const named = change !== null || costMode;
  const changed = useQuery({
    ...outcomesByRefQuery(
      change?.change === 'removed' ? (run.data?.diff_base_run_id ?? runId) : runId,
      changedRefs.slice(0, IMPORT_PAGE_MAX),
    ),
    enabled: named && changedRefs.length > 0,
  });
  const list = useInfiniteQuery({
    ...outcomesQuery(runId, { outcome: search.outcome, kind: search.kind, q: search.q }),
    placeholderData: keepPreviousData,
    enabled: !named,
  });
  useDocumentTitle('Source records');

  // The search box types freely; the URL (and the server search) follows after a pause.
  const [text, setText] = useState(search.q ?? '');
  const pushed = useRef(search.q ?? '');
  useEffect(() => {
    if ((search.q ?? '') !== pushed.current) {
      pushed.current = search.q ?? '';
      setText(search.q ?? '');
    }
  }, [search.q]);
  useEffect(() => {
    const q = text.trim();
    if (q === pushed.current) return;
    const timer = setTimeout(() => {
      pushed.current = q;
      void navigate({ search: (prev) => ({ ...prev, q: q || undefined }), replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, navigate]);

  const items = named
    ? (changed.data?.items ?? [])
    : (list.data?.pages.flatMap((p) => p.items) ?? []);
  const total = named ? changedRefs.length : (list.data?.pages[0]?.total ?? 0);
  const title = change
    ? `${DIFF_SECTION[change.section]}: ${CHANGE_TITLE[change.change].toLowerCase()} since the last load`
    : costMode
      ? 'Products without a standard cost'
      : search.outcome
        ? OUTCOME[search.outcome].label
        : 'Every source record';
  const pending = named
    ? (costMode && run.isPending) || (changed.isPending && changedRefs.length > 0)
    : list.isPending;
  const failedList = named ? changed.isError && !changed.data : list.isError && !list.data;

  return (
    <>
      <PageHeader
        eyebrow={
          <ImportsBreadcrumb
            run={
              run.data
                ? { id: runId, label: runLabel(run.data, timeZone) }
                : { id: runId, label: 'Import' }
            }
            page="Source records"
          />
        }
        title={title}
        description="What happened to each item and variant read from the source."
        actions={
          <Button variant="secondary" asChild>
            <Link to="/imports/runs/$runId" params={{ runId }}>
              <ArrowLeft aria-hidden />
              Back to the report
            </Link>
          </Button>
        }
      />
      {named ? (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={<X aria-hidden />}
            onClick={() => void navigate({ search: {}, replace: true })}
          >
            Show every record
          </Button>
        </div>
      ) : (
        <div
          className="mb-3 flex flex-wrap items-center gap-2"
          role="search"
          aria-label="Filter records"
        >
          <div className="relative w-full min-w-44 sm:w-auto sm:max-w-72 sm:flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
            />
            <Input
              type="search"
              aria-label="Search records"
              placeholder="Name or SKU"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select
            value={search.outcome ?? ALL}
            onValueChange={(v) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  outcome: v === ALL ? undefined : (v as OutcomeKind),
                }),
                replace: true,
              })
            }
          >
            <SelectTrigger aria-label="Outcome" className="w-auto min-w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Every outcome</SelectItem>
              {OUTCOME_ORDER.map((k) => (
                <SelectItem key={k} value={k}>
                  {OUTCOME[k].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={search.kind ?? ALL}
            onValueChange={(v) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  kind: v === ALL ? undefined : (v as 'item' | 'variant'),
                }),
                replace: true,
              })
            }
          >
            <SelectTrigger aria-label="Record" className="w-auto min-w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Items and variants</SelectItem>
              <SelectItem value="item">Items</SelectItem>
              <SelectItem value="variant">Variants</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {failedList ? (
        <ErrorPanel
          error={named ? changed.error : list.error}
          titleAs="h2"
          what="The records"
          onRetry={() => void (named ? changed.refetch() : list.refetch())}
        />
      ) : (
        <DataTable
          label="Source records"
          columns={columns}
          data={items}
          loading={pending}
          filterable={false}
          getRowId={(o) => o.source_ref}
          empty={
            <EmptyState
              compact
              icon={SearchX}
              title="No records here"
              description={
                search.q
                  ? `No record’s name or SKU contains “${search.q}”.`
                  : search.outcome === 'could_not_classify'
                    ? 'Every record is classified.'
                    : 'No source record has this outcome.'
              }
            />
          }
          footer={
            items.length > 0 ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-muted" aria-live="polite">
                  {items.length >= total
                    ? `${formatCount(total)} ${total === 1 ? 'record' : 'records'}`
                    : `Showing ${formatCount(items.length)} of ${formatCount(total)} records`}
                </p>
                {list.hasNextPage && (
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={list.isFetchingNextPage}
                    onClick={() => void list.fetchNextPage()}
                  >
                    {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                )}
              </div>
            ) : undefined
          }
          className="max-h-[calc(100dvh-17rem)]"
        />
      )}
    </>
  );
}
